/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { artifactService } from '../services/artifact.service';
import { EditImpactDialog, shouldConfirmEdit } from '../components/shared/EditImpactDialog';
import type { ArtifactEditImpact, ArtifactTypeName } from '../types/artifact-usage';

/** The update being guarded, as a React Query mutation exposes it. */
export interface GuardableUpdate<V, O> {
  mutate: (vars: V, options?: O) => void;
  isPending: boolean;
  isError: boolean;
  error: unknown;
  reset: () => void;
}

/** What the guard asks about, for one type. */
export interface EditImpactTarget<V> {
  type: ArtifactTypeName;
  /** What one of these is called, for the dialog's sentences. */
  noun: { one: string; many: string };
  /** The artifact the update writes, and the document or patch it posts. */
  target: (vars: V) => { id: string | number; proposed: object };
}

/**
 * **Update an artifact, having first said what that would change elsewhere** (`P4` slice 2,
 * N9d · #902 rule 2), for any type with children (slice 7a; it was demand sources only).
 *
 * The guard lives in a **hook** every save of the type goes through, not in the surfaces that save:
 *
 * > *A rule attached to one write path lapses the moment another is added. It belongs to the act of
 * > writing, not to the shape of the request.*
 *
 * ## It interrupts only when there is something to say
 *
 * An artifact with no children, or an edit every child shields, saves with **no dialog** — see
 * {@link shouldConfirmEdit}. A confirm that fires on every save is one the operator clicks through,
 * and then the one that matters goes with it.
 *
 * ## The dialog is returned, not portalled
 *
 * The caller renders `impactDialog` where it makes sense in its own surface. *Whether* to ask is here,
 * *where the pixels go* is theirs. A portal from a hook would also escape a wizard's own Escape
 * handling, which is exactly the trap `ConfirmDialog` was built after (#1129).
 */
export function useEditImpactGuard<V, O>(update: GuardableUpdate<V, O>, about: EditImpactTarget<V>) {
  const [pending, setPending] = React.useState<{
    vars: V;
    options?: O;
    impact: ArtifactEditImpact | null;
    failed: boolean;
    artifactName?: string;
  } | null>(null);
  const [isChecking, setChecking] = React.useState(false);

  const mutate = (vars: V, options?: O, artifactName?: string) => {
    setChecking(true);
    const { id, proposed } = about.target(vars);
    artifactService
      .editImpact(about.type, id, proposed)
      .then((impact) => {
        setChecking(false);
        if (!shouldConfirmEdit(impact)) {
          update.mutate(vars, options);
          return;
        }
        setPending({ vars, options, impact, failed: false, artifactName });
      })
      .catch(() => {
        setChecking(false);
        // Neither silent nor blocked: the operator is told the check failed and decides. Blocking
        // would make a transient error read as a permission problem; saving quietly would claim
        // nothing is affected on the strength of a question that failed.
        setPending({ vars, options, impact: null, failed: true, artifactName });
      });
  };

  const impactDialog = pending ? (
    <EditImpactDialog
      impact={pending.impact}
      failed={pending.failed}
      artifactName={pending.artifactName}
      noun={about.noun}
      onConfirm={() => {
        const { vars, options } = pending;
        setPending(null);
        update.mutate(vars, options);
      }}
      onCancel={() => setPending(null)}
    />
  ) : null;

  return {
    mutate,
    // `isChecking` counts as pending so a Save button disables while the preview is in flight —
    // otherwise a second click starts a second check and can queue two saves behind one dialog.
    isPending: update.isPending || isChecking || pending !== null,
    isError: update.isError,
    error: update.error,
    reset: () => {
      setPending(null);
      update.reset();
    },
    impactDialog,
  };
}
